import { ThemeComponent } from "@/theme/ThemeComponent";
import { renderAsChild } from "@/utils/LitHelpers";
import { Component, Prop, Ref } from "@eastsideco/salvo-ts";
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { html } from "lit-html";


const navigationDot = (index: number) => {
    const extraClass = index ? '' : 'image-scroller__navigation__dot--active';
    return html`
        <div class=${`image-scroller__navigation__dot ${extraClass}`} data-nav-index=${index}></div>
    `;
};

const navigationDots = (count: number) => html`
<div class="navigation-dots-wrapper">
    <div class="image-scroller__navigation">
        ${Array(count).fill(0).map((value, index) => navigationDot(index))}
    </div>
</div>
`;

@Component
export class ImageScroller extends ThemeComponent {
    @Ref images!: HTMLDivElement[];
    @Prop listenToVariantChange!: boolean;
    @Prop optionIndex!: number;

    intersectionObserver?: IntersectionObserver;
    navigationDots?: HTMLElement[];

    init() {
        if (this.images.length > 1) {
            renderAsChild(navigationDots(this.images.length), this);
            this.intersectionObserver = new IntersectionObserver(this.onViewportIntersected.bind(this), {
                threshold: 0.7
            });

            this.navigationDots = Array.from(this.querySelectorAll('.image-scroller__navigation__dot'));

            for (const dot of this.navigationDots) {
                this.$listen(dot as HTMLDivElement, 'click', this.onNavigationClicked.bind(this));
            }

            for (const image of this.images) {
                this.intersectionObserver.observe(image);
            }

            // Handle variant changes
            if (this.listenToVariantChange && this.optionIndex !== -1) {
                const productOptionPicker = document.getElementById('product-form-option-picker');
                if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                    const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
                    // eslint-disable-next-line max-len
                    this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant | null) => {
                        let imageId = null;
                        if (variant?.featured_image) {
                            imageId = variant?.featured_image.id;
                        }
                        this.handleOnVariantChange(variant, imageId);
                    });
                }
            }
        }
    }

    destroy() {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }

    private onNavigationClicked(event: MouseEvent) {
        const target = event.target as HTMLDivElement;
        const index = parseInt(target.getAttribute('data-nav-index') as string, 10);
        this.scrollToIndex(index);
    }

    private setActiveDot(index: number) {
        if (this.navigationDots) {
            const activeDot = this.querySelector('.image-scroller__navigation__dot--active') as HTMLElement;
            activeDot.classList.remove('image-scroller__navigation__dot--active');

            const targetDot = this.navigationDots[index] as HTMLElement;
            targetDot.classList.add('image-scroller__navigation__dot--active');
        }
    }

    private scrollToIndex(index: number) {
        this.images[index]?.scrollIntoView({block: 'center'});
        this.setActiveDot(index);
    }


    private onViewportIntersected(intersections: IntersectionObserverEntry[]) {
        // Put the images with the greatest visible portion first
        const sortedIntersections = intersections.sort((a, b) => {
            if (a.intersectionRatio > b.intersectionRatio) {
                return -1;
            }
            else if (a.intersectionRatio < b.intersectionRatio) {
                return 1;
            }
            else {
                return 0;
            }
        });

        if (sortedIntersections.length >= 1 && sortedIntersections[0].isIntersecting) {
            const image = sortedIntersections[0].target as HTMLElement;

            let imageIndex: number;
            if (this.listenToVariantChange) {
                imageIndex = Array.from(this.children).indexOf(image);
            } else {
                imageIndex = parseInt(image.getAttribute('data-image-index') as string, 10);
            }
            this.setActiveDot(imageIndex);
        }
    }

    private handleOnVariantChange(variant: Shopify.Liquid.Variant | null, imageId: number | null) {
        // If we have a matching variant, re-insert it as the first child
        const variantImage = this.querySelector(`[data-image-id="${imageId}"]`);
        if (variantImage != null) {
            this.insertBefore(variantImage, this.children[0]);
        }
    }


}
